import { z } from "zod";
import { pricePaiseSchema } from "./catalog";
import { imageStylePresetSchema } from "./restaurant";

/**
 * AI tools (Phase 8): menu import, item images, website generation and the
 * description writer. Superadmin only. Every result is a draft a person
 * reviews before anything changes. See docs/architecture/ai.md.
 */

export const AI_JOB_KINDS = ["menu_import", "item_image", "site_generate", "copy"] as const;
export const aiJobKindSchema = z.enum(AI_JOB_KINDS);
export type AiJobKind = z.infer<typeof aiJobKindSchema>;

export const AI_JOB_STATUSES = ["queued", "running", "succeeded", "failed"] as const;
export const aiJobStatusSchema = z.enum(AI_JOB_STATUSES);
export type AiJobStatus = z.infer<typeof aiJobStatusSchema>;

/** `failed`: the extraction job failed; nothing to review. */
export const MENU_IMPORT_STATUSES = [
	"processing",
	"review",
	"applied",
	"discarded",
	"failed",
] as const;
export const menuImportStatusSchema = z.enum(MENU_IMPORT_STATUSES);
export type MenuImportStatus = z.infer<typeof menuImportStatusSchema>;

/** Per job limits (ai.md, Cost control). */
export const AI_LIMITS = {
	/** Pages of one menu import: image files, plus the pages of each PDF. */
	menuImportPages: 20,
	/** Images in one bulk generation. */
	imageBatch: 100,
	/** Pages read at once, and images made at once. */
	parallel: 4,
	/** Items in one description request. */
	copyBatch: 50,
	/** The description writer's limit (ai.md, Feature 4). */
	descriptionChars: 120,
	/** The admin's brief or instruction for the website. */
	siteBrief: 600,
} as const;

// ---------------------------------------------------------------------------
// Menu import
// ---------------------------------------------------------------------------

export const MENU_IMPORT_DIETS = ["veg", "nonveg", "egg", "unknown"] as const;
export const menuImportDietSchema = z.enum(MENU_IMPORT_DIETS);
export type MenuImportDiet = z.infer<typeof menuImportDietSchema>;

export const CONFIDENCES = ["high", "medium", "low"] as const;
export const confidenceSchema = z.enum(CONFIDENCES);
export type Confidence = z.infer<typeof confidenceSchema>;

/**
 * What the vision model returns for one page (ai.md, Output schema). Kept
 * lenient on lengths: the merge step trims to the catalog's limits, so a
 * long name never costs a retry.
 */
export const menuImportPageSchema = z.object({
	categories: z.array(
		z.object({
			name: z.string(),
			items: z.array(
				z.object({
					name: z.string(),
					description: z.string().nullable(),
					diet: menuImportDietSchema,
					/** When there are no variants. */
					pricePaise: z.number().int().min(0).nullable(),
					variants: z.array(z.object({ name: z.string(), pricePaise: z.number().int().min(0) })),
					confidence: confidenceSchema,
					/** Anything unclear. */
					note: z.string().nullable(),
				}),
			),
		}),
	),
	currencyAssumed: z.literal("INR"),
	warnings: z.array(z.string()),
});
export type MenuImportPage = z.infer<typeof menuImportPageSchema>;

const text = (max: number) => z.string().trim().min(1).max(max);

export const menuImportDraftVariantSchema = z.object({
	name: text(40),
	pricePaise: pricePaiseSchema,
});

/** One dish in the reviewer's table. `id` only keys the row while editing. */
export const menuImportDraftItemSchema = z.object({
	id: z.uuid(),
	name: text(80),
	description: z.string().trim().max(500).nullable(),
	diet: menuImportDietSchema,
	/** Without variants a price is needed before applying. */
	pricePaise: pricePaiseSchema.nullable(),
	variants: z.array(menuImportDraftVariantSchema).max(20),
	confidence: confidenceSchema,
	note: z.string().trim().max(300).nullable(),
	/** The source page it came from, 1 based, for showing it beside the row. */
	page: z.number().int().min(1).max(AI_LIMITS.menuImportPages).nullable(),
});
export type MenuImportDraftItem = z.infer<typeof menuImportDraftItemSchema>;

export const menuImportDraftCategorySchema = z.object({
	id: z.uuid(),
	name: text(60),
	items: z.array(menuImportDraftItemSchema).max(300),
});
export type MenuImportDraftCategory = z.infer<typeof menuImportDraftCategorySchema>;

export const menuImportDraftSchema = z.object({
	categories: z.array(menuImportDraftCategorySchema).max(100),
	warnings: z.array(z.string().max(500)).max(100),
});
export type MenuImportDraft = z.infer<typeof menuImportDraftSchema>;

/** Why a draft cannot be applied yet, by row. Empty: ready. */
export type MenuImportProblem =
	| { kind: "diet_unknown"; categoryId: string; itemId: string }
	| { kind: "no_price"; categoryId: string; itemId: string }
	| { kind: "empty" };

/** The problems that block Apply: no dishes, a dish with diet `unknown` (AD-04), or without a price. */
export function menuImportProblems(draft: MenuImportDraft): MenuImportProblem[] {
	const problems: MenuImportProblem[] = [];
	let count = 0;
	for (const category of draft.categories) {
		for (const item of category.items) {
			count++;
			if (item.diet === "unknown") {
				problems.push({ kind: "diet_unknown", categoryId: category.id, itemId: item.id });
			}
			if (item.variants.length === 0 && item.pricePaise === null) {
				problems.push({ kind: "no_price", categoryId: category.id, itemId: item.id });
			}
		}
	}
	if (count === 0) problems.push({ kind: "empty" });
	return problems;
}

export const MENU_IMPORT_APPLY_MODES = ["add", "replace"] as const;
export const menuImportApplyModeSchema = z.enum(MENU_IMPORT_APPLY_MODES);
export type MenuImportApplyMode = z.infer<typeof menuImportApplyModeSchema>;

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export const ITEM_IMAGE_RESULT_STATES = ["draft", "accepted", "discarded", "failed"] as const;
export const itemImageResultStateSchema = z.enum(ITEM_IMAGE_RESULT_STATES);
export type ItemImageResultState = z.infer<typeof itemImageResultStateSchema>;

export const itemImageResultSchema = z.object({
	itemId: z.uuid(),
	/** The generated photo, stored with `is_ai_generated`. Null until made, or when it failed. */
	fileId: z.uuid().nullable(),
	state: itemImageResultStateSchema,
	error: z.string().nullable(),
});
export type ItemImageResult = z.infer<typeof itemImageResultSchema>;

export const copyResultSchema = z.object({
	itemId: z.uuid(),
	name: z.string(),
	before: z.string().nullable(),
	description: z.string().max(AI_LIMITS.descriptionChars).nullable(),
	/** Written to the item by `applyDescriptions`. */
	applied: z.boolean(),
});
export type CopyResult = z.infer<typeof copyResultSchema>;

/** What a finished job produced, by kind. Stored in `ai_jobs.output`. */
export const aiJobResultSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("menu_import"),
		importId: z.uuid(),
		/** Each page read, in order: page n is `pages[n - 1]`; `pdfPage` is 1 based, null for a photo. */
		pages: z.array(z.object({ fileId: z.uuid(), pdfPage: z.number().int().nullable() })),
		items: z.number().int(),
	}),
	z.object({
		kind: z.literal("item_image"),
		mode: z.enum(["generate", "enhance"]),
		results: z.array(itemImageResultSchema),
	}),
	z.object({
		kind: z.literal("site_generate"),
		/** The draft revision the job wrote. */
		revision: z.number().int(),
		/** True once the admin went back to the draft from before the job. */
		undone: z.boolean(),
	}),
	z.object({ kind: z.literal("copy"), results: z.array(copyResultSchema) }),
]);
export type AiJobResult = z.infer<typeof aiJobResultSchema>;

export const aiJobSchema = z.object({
	id: z.uuid(),
	restaurantId: z.uuid(),
	restaurantName: z.string().nullable(),
	kind: aiJobKindSchema,
	status: aiJobStatusSchema,
	/** The main model, or the fallback when it was used. */
	model: z.string().nullable(),
	promptTokens: z.number().int(),
	completionTokens: z.number().int(),
	costMicroUsd: z.number().int(),
	/** A sentence for the admin when the job failed. */
	error: z.string().nullable(),
	progress: z.object({ done: z.number().int(), total: z.number().int() }),
	result: aiJobResultSchema.nullable(),
	createdBy: z.uuid().nullable(),
	createdAt: z.iso.datetime(),
	startedAt: z.iso.datetime().nullable(),
	finishedAt: z.iso.datetime().nullable(),
});
export type AiJob = z.infer<typeof aiJobSchema>;

// ---------------------------------------------------------------------------
// Procedure inputs and outputs: `admin.ai.*`
// ---------------------------------------------------------------------------

const restaurantRef = { restaurantId: z.uuid() };

export const startMenuImportInputSchema = z.object({
	...restaurantRef,
	/** Made by the client: the same call again returns the same import. */
	importId: z.uuid(),
	/** Ready `menu_source` uploads of the restaurant, in page order. */
	sourceFileIds: z
		.array(z.uuid())
		.min(1)
		.max(AI_LIMITS.menuImportPages)
		.refine((ids) => new Set(ids).size === ids.length, "Each file once"),
});

export const menuImportSourceSchema = z.object({
	fileId: z.uuid(),
	mime: z.string(),
	/** A five minute signed link to show the page beside the table. */
	url: z.string(),
});

export const menuImportSchema = z.object({
	id: z.uuid(),
	restaurantId: z.uuid(),
	status: menuImportStatusSchema,
	sources: z.array(menuImportSourceSchema),
	draft: menuImportDraftSchema.nullable(),
	revision: z.number().int(),
	job: aiJobSchema,
	appliedAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
});
export type MenuImport = z.infer<typeof menuImportSchema>;

export const menuImportSummarySchema = z.object({
	id: z.uuid(),
	status: menuImportStatusSchema,
	pages: z.number().int(),
	items: z.number().int(),
	createdAt: z.iso.datetime(),
});
export type MenuImportSummary = z.infer<typeof menuImportSummarySchema>;

export const menuImportRefInputSchema = z.object({ ...restaurantRef, importId: z.uuid() });

export const updateMenuImportDraftInputSchema = z.object({
	...restaurantRef,
	importId: z.uuid(),
	/** The revision the reviewer edited; another one is `CONFLICT`, `stale_draft`. */
	baseRevision: z.number().int(),
	draft: menuImportDraftSchema,
});

export const applyMenuImportInputSchema = z.object({
	...restaurantRef,
	importId: z.uuid(),
	baseRevision: z.number().int(),
	/** `add`: next to the menu (same-named categories are joined). `replace`: the old menu is soft deleted. */
	mode: menuImportApplyModeSchema,
});

export const applyMenuImportResultSchema = z.object({
	importId: z.uuid(),
	categories: z.number().int(),
	items: z.number().int(),
	removedItems: z.number().int(),
});

export const generateItemImagesInputSchema = z.object({
	...restaurantRef,
	jobId: z.uuid(),
	itemIds: z
		.array(z.uuid())
		.min(1)
		.max(AI_LIMITS.imageBatch)
		.refine((ids) => new Set(ids).size === ids.length, "Each dish once"),
	/** The premium image model (tech-stack.md), chosen per request. */
	premium: z.boolean().default(false),
});

export const enhanceItemImageInputSchema = z.object({
	...restaurantRef,
	jobId: z.uuid(),
	itemId: z.uuid(),
	/** A ready `item_image` of the restaurant: the dish's photo or a fresh upload. */
	sourceFileId: z.uuid(),
});

export const itemImageRefInputSchema = z.object({
	...restaurantRef,
	jobId: z.uuid(),
	itemId: z.uuid(),
});

export const imageSettingsSchema = z.object({
	imageStyle: imageStylePresetSchema,
	/** The storefront's "Representative image" note under generated photos (P8-T20). */
	showAiImageNote: z.boolean(),
});
export type ImageSettings = z.infer<typeof imageSettingsSchema>;

export const setImageSettingsInputSchema = imageSettingsSchema
	.partial()
	.extend(restaurantRef)
	.refine(
		(v) => v.imageStyle !== undefined || v.showAiImageNote !== undefined,
		"Nothing to change",
	);

const studioImageSchema = z.object({ id: z.uuid(), blurhash: z.string().nullable() });

export const imageStudioSchema = z.object({
	settings: imageSettingsSchema,
	items: z.array(
		z.object({
			id: z.uuid(),
			name: z.string(),
			category: z.string(),
			description: z.string().nullable(),
			image: studioImageSchema.nullable(),
			imageIsAi: z.boolean(),
		}),
	),
	/** Generated photos waiting for Accept or Discard, newest first. */
	drafts: z.array(
		z.object({
			jobId: z.uuid(),
			itemId: z.uuid(),
			image: studioImageSchema,
			createdAt: z.iso.datetime(),
		}),
	),
	/** Image jobs still queued or running. */
	running: z.array(aiJobSchema),
});
export type ImageStudio = z.infer<typeof imageStudioSchema>;

export const generateSiteInputSchema = z.object({
	...restaurantRef,
	jobId: z.uuid(),
	/** A few words such as "family place, famous for thali, warm and traditional". */
	brief: z.string().trim().max(AI_LIMITS.siteBrief),
});

export const editSiteInputSchema = z.object({
	...restaurantRef,
	jobId: z.uuid(),
	/** "shorter about text", "make it more premium". */
	instruction: z.string().trim().min(1).max(AI_LIMITS.siteBrief),
});

export const jobRefInputSchema = z.object({ ...restaurantRef, jobId: z.uuid() });

export const writeDescriptionsInputSchema = z.object({
	...restaurantRef,
	jobId: z.uuid(),
	itemIds: z
		.array(z.uuid())
		.min(1)
		.max(AI_LIMITS.copyBatch)
		.refine((ids) => new Set(ids).size === ids.length, "Each dish once"),
});

export const applyDescriptionsInputSchema = z.object({
	...restaurantRef,
	jobId: z.uuid(),
	/** The descriptions to keep, as the admin edited them. */
	items: z
		.array(
			z.object({
				itemId: z.uuid(),
				description: z.string().trim().min(1).max(AI_LIMITS.descriptionChars),
			}),
		)
		.min(1)
		.max(AI_LIMITS.copyBatch),
});

export const listAiJobsInputSchema = z.object({
	restaurantId: z.uuid().optional(),
	kind: aiJobKindSchema.optional(),
	status: aiJobStatusSchema.optional(),
	/** The last ID of the previous page. */
	cursor: z.uuid().optional(),
	limit: z.number().int().min(1).max(100).default(50),
});

export const aiJobPageSchema = z.object({
	items: z.array(aiJobSchema),
	nextCursor: z.uuid().nullable(),
});

export const AI_BUDGET_LEVELS = ["ok", "warning", "exhausted"] as const;
export type AiBudgetLevel = (typeof AI_BUDGET_LEVELS)[number];

/** Warning at 80% of the monthly budget, refusal at 100% (ai.md, Cost control). */
export const AI_BUDGET_WARNING_RATIO = 0.8;

export const aiBudgetSchema = z.object({
	/** The calendar month in UTC, `YYYY-MM`. */
	month: z.string(),
	spentMicroUsd: z.number().int(),
	budgetMicroUsd: z.number().int(),
	level: z.enum(AI_BUDGET_LEVELS),
});
export type AiBudget = z.infer<typeof aiBudgetSchema>;

export function aiBudgetLevel(spentMicroUsd: number, budgetMicroUsd: number): AiBudgetLevel {
	if (spentMicroUsd >= budgetMicroUsd) return "exhausted";
	if (spentMicroUsd >= budgetMicroUsd * AI_BUDGET_WARNING_RATIO) return "warning";
	return "ok";
}

export const aiUsageInputSchema = z.object({
	/** `YYYY-MM-DD`, inclusive, UTC. */
	from: z.iso.date(),
	to: z.iso.date(),
});

const spendRow = {
	costMicroUsd: z.number().int(),
	jobs: z.number().int(),
	promptTokens: z.number().int(),
	completionTokens: z.number().int(),
};

export const aiUsageSchema = z.object({
	budget: aiBudgetSchema,
	total: z.object(spendRow),
	byDay: z.array(z.object({ date: z.string(), ...spendRow })),
	byKind: z.array(z.object({ kind: aiJobKindSchema, ...spendRow })),
	byRestaurant: z.array(
		z.object({ restaurantId: z.uuid(), name: z.string().nullable(), ...spendRow }),
	),
	byModel: z.array(z.object({ model: z.string(), ...spendRow })),
});
export type AiUsage = z.infer<typeof aiUsageSchema>;

/**
 * Per model spend of a job, in `ai_jobs.usage`: a job may call the main
 * model and its fallback, so the usage page can split spend by model.
 */
export const aiJobUsageSchema = z.record(
	z.string(),
	z.object({
		calls: z.number().int(),
		promptTokens: z.number().int(),
		completionTokens: z.number().int(),
		costMicroUsd: z.number().int(),
	}),
);
export type AiJobUsage = z.infer<typeof aiJobUsageSchema>;
